// src/theme/themeConfig.ts
// 主题配置

export interface ThemeConfig {
    key: string
    nameKey: string  // 多语言 key，格式: theme.name.xxx
    colors: {
        primary: string
        primaryBg: string
        sidebarBg: string
        sidebarHoverBg: string
        contentBg: string
        cardBg: string
        borderColor: string
        textColor: string
        textSecondary: string
        tableRowHover: string
        inputBg: string
        headerBg: string
    }
}

export const themes: ThemeConfig[] = [
    {
        key: 'default',
        nameKey: 'theme.name.default',
        colors: {
            primary: '#868a8e',
            primaryBg: '#e6f7ff',
            sidebarBg: '#ffffff',
            sidebarHoverBg: '#f5f5f5',
            contentBg: '#f5f7fa',
            cardBg: '#ffffff',
            borderColor: '#f0f0f0',
            textColor: '#262626',
            textSecondary: '#8c8c8c',
            tableRowHover: '#fafafa',
            inputBg: '#ffffff',
            headerBg: '#ffffff',
        },
    },
    {
        key: 'skyblue',
        nameKey: 'theme.name.skyblue',
        colors: {
            primary: '#68b2f7',
            primaryBg: '#e6f7ff',
            sidebarBg: '#f0f8ff',
            sidebarHoverBg: '#e0eff8',
            contentBg: '#e8f4fa',
            cardBg: '#f4fafc',
            borderColor: '#d0e4ec',
            textColor: '#1a2a3a',
            textSecondary: '#6a8a9a',
            tableRowHover: '#ecf5fa',
            inputBg: '#f4fafc',
            headerBg: '#f0f8ff',
        },
    },
    {
        key: 'green',
        nameKey: 'theme.name.green',
        colors: {
            primary: '#52c41a',
            primaryBg: '#e8f5e0',
            sidebarBg: '#f0f7ec',
            sidebarHoverBg: '#e0efe0',
            contentBg: '#e8f0e4',
            cardBg: '#f4faf0',
            borderColor: '#c8dcc8',
            textColor: '#1a3a1a',
            textSecondary: '#5a7a5a',
            tableRowHover: '#e8f5e8',
            inputBg: '#f4faf0',
            headerBg: '#f0f7ec',
        },
    },
    {
        key: 'orange',
        nameKey: 'theme.name.orange',
        colors: {
            primary: '#fa8c16',
            primaryBg: '#fef0e0',
            sidebarBg: '#fcf5e8',
            sidebarHoverBg: '#f8ece0',
            contentBg: '#f8f0e8',
            cardBg: '#fcf8f0',
            borderColor: '#f0e0d0',
            textColor: '#3a2a1a',
            textSecondary: '#8a7a6a',
            tableRowHover: '#faf0e8',
            inputBg: '#fcf8f0',
            headerBg: '#fcf5e8',
        },
    },
    {
        key: 'purple',
        nameKey: 'theme.name.purple',
        colors: {
            primary: '#722ed1',
            primaryBg: '#f0e8f8',
            sidebarBg: '#f5ecfa',
            sidebarHoverBg: '#ece0f5',
            contentBg: '#f0e8f5',
            cardBg: '#f8f4fc',
            borderColor: '#e0d0e8',
            textColor: '#2a1a3a',
            textSecondary: '#7a6a8a',
            tableRowHover: '#f5ecfa',
            inputBg: '#f8f4fc',
            headerBg: '#f5ecfa',
        },
    },
    {
        key: 'pink',
        nameKey: 'theme.name.pink',
        colors: {
            primary: '#eb2f96',
            primaryBg: '#fce8f0',
            sidebarBg: '#fcf0f5',
            sidebarHoverBg: '#f8e0ec',
            contentBg: '#f8ecf0',
            cardBg: '#fcf4f8',
            borderColor: '#f0d8e0',
            textColor: '#3a1a2a',
            textSecondary: '#8a6a7a',
            tableRowHover: '#fcf0f5',
            inputBg: '#fcf4f8',
            headerBg: '#fcf0f5',
        },
    },
    {
        key: 'teal',
        nameKey: 'theme.name.teal',
        colors: {
            primary: '#13c2c2',
            primaryBg: '#e0f5f5',
            sidebarBg: '#ecf8f8',
            sidebarHoverBg: '#ddf0f0',
            contentBg: '#e8f4f4',
            cardBg: '#f0fafc',
            borderColor: '#c8e0e0',
            textColor: '#1a3a3a',
            textSecondary: '#5a7a7a',
            tableRowHover: '#e8f5f5',
            inputBg: '#f0fafc',
            headerBg: '#ecf8f8',
        },
    },
    {
        key: 'warm',
        nameKey: 'theme.name.warm',
        colors: {
            primary: '#fadb14',
            primaryBg: '#fcf8e0',
            sidebarBg: '#fcf5d8',
            sidebarHoverBg: '#f8ecd0',
            contentBg: '#f8f0d8',
            cardBg: '#fcf8ec',
            borderColor: '#f0e8c8',
            textColor: '#3a3a1a',
            textSecondary: '#8a8a6a',
            tableRowHover: '#fcf5e0',
            inputBg: '#fcf8ec',
            headerBg: '#fcf5d8',
        },
    },
    {
        key: 'forest',
        nameKey: 'theme.name.forest',
        colors: {
            primary: '#2d8f47',
            primaryBg: '#e0f0e4',
            sidebarBg: '#e8f0e0',
            sidebarHoverBg: '#d8e8d0',
            contentBg: '#e0ece0',
            cardBg: '#f0f8f0',
            borderColor: '#c0d8c0',
            textColor: '#1a2a1a',
            textSecondary: '#5a7a5a',
            tableRowHover: '#e8f4e8',
            inputBg: '#f0f8f0',
            headerBg: '#e8f0e0',
        },
    },
]

// 应用主题
export const applyTheme = (themeKey: string) => {
    const theme = themes.find(t => t.key === themeKey)
    if (!theme) return

    const root = document.documentElement
    const colors = theme.colors

    root.style.setProperty('--primary-color', colors.primary)
    root.style.setProperty('--primary-bg', colors.primaryBg)
    root.style.setProperty('--sidebar-bg', colors.sidebarBg)
    root.style.setProperty('--sidebar-hover-bg', colors.sidebarHoverBg)
    root.style.setProperty('--content-bg', colors.contentBg)
    root.style.setProperty('--card-bg', colors.cardBg)
    root.style.setProperty('--border-color', colors.borderColor)
    root.style.setProperty('--text-color', colors.textColor)
    root.style.setProperty('--text-secondary', colors.textSecondary)
    root.style.setProperty('--table-row-hover', colors.tableRowHover)
    root.style.setProperty('--input-bg', colors.inputBg)
    root.style.setProperty('--header-bg', colors.headerBg)

    localStorage.setItem('theme', themeKey)
}

// 加载保存的主题
export const loadTheme = () => {
    const savedTheme = localStorage.getItem('theme') || 'default'
    applyTheme(savedTheme)
    return savedTheme
}

// 获取当前主题配置
export const getCurrentTheme = (): ThemeConfig | undefined => {
    const themeKey = localStorage.getItem('theme') || 'default'
    return themes.find(t => t.key === themeKey)
}

// 获取主题名称（通过 t 函数）
export const getThemeName = (themeKey: string, t: (key: string) => string): string => {
    const theme = themes.find(t => t.key === themeKey)
    if (!theme) return themeKey
    return t(theme.nameKey) || themeKey
}